import forge from 'apis/forge';
import SceneConversation from 'components/Panel/Chat/Conversation/SceneConversation';
import { Loader } from 'components/Widget/Loader';
import { NEW_CHAT_TITLE } from 'dragonmouse-shared/constants';
import { CHAT_COLUMN_GAP, ChatStatus } from 'dragonmouse-shared/interfaces';
import { MenuItemAction } from 'menu/MenuItem';
import { useEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { selectActiveSceneChat, selectActiveScene, selectSceneChatRefs } from 'redux/db/reducers';
import { canEdit, getRightmostFrameX } from 'redux/db/util';
import { setActiveSceneChatId } from 'redux/mode/actions';
import { displayViewOnlyModal } from 'redux/mode/util';
import { RootReducer } from 'redux/reducers';
import { v4 as uuid } from 'uuid';
import { Header } from '../Header';
import * as Base from '../styles';

const SceneChatPanel = () => {
	const dispatch = useDispatch();
	const [isCreatingChat, setIsCreatingChat] = useState(false);

	const ref = useRef() as React.MutableRefObject<HTMLDivElement>;

	const { chatRefs, activeSceneChatId, activeScene, activeChatTitle, chatsLoading } =
		useSelector((r: RootReducer) => {
			const chat = selectActiveSceneChat(r.db, r.mode.activeSceneChatId);
			const chatRefs = selectSceneChatRefs(r.db);
			const activeSceneChatId = r.mode.activeSceneChatId;

			// Check if activeSceneChatId is in chatRefs
			const activeChatIdInRefs = activeSceneChatId
				? chatRefs.some((ref) => ref.id === activeSceneChatId)
				: true; // If no active chat, consider it "in refs" (not loading)

			return {
				chatRefs,
				activeSceneChatId,
				activeScene: selectActiveScene(r.db, r.mode.activeSceneId),
				activeChatTitle: chat?.title || 'Untitled Chat',
				chatsLoading:
					r.db.loading.activeScene.chats !== 'loaded' ||
					(activeSceneChatId !== null && chat === null) ||
					(activeSceneChatId !== null && !activeChatIdInRefs),
			};
		},
		shallowEqual
	);
	const sortedChatRefs = useMemo(() => {
		// Chat refs are already sorted by updatedAt DESC from the backend
		return chatRefs;
	}, [chatRefs]);

	const moreMenuItems = useMemo(() => {
		if (!activeSceneChatId) return [];
		return [
			MenuItemAction.createRenameChat(activeSceneChatId),
			MenuItemAction.createSelectFramesFromChat(activeSceneChatId),
			MenuItemAction.createCopyChatId(activeSceneChatId),
		];
	}, [activeSceneChatId]);

	useEffect(() => {
		// Don't auto-select when chats are loading - this includes the case when
		// a new chat is being created and hasn't appeared in chatRefs yet
		if (chatsLoading) {
			return;
		}

		const chatRefIds = sortedChatRefs.map((ref) => ref.id);
		const activeChatIdInRefs = activeSceneChatId ? chatRefIds.includes(activeSceneChatId) : false;

		if (!activeSceneChatId || !activeChatIdInRefs) {
			const firstChatRef = sortedChatRefs.length > 0 ? sortedChatRefs[0] : null;
			if (firstChatRef) {
				dispatch(setActiveSceneChatId(firstChatRef.id));
			}
		}
	}, [sortedChatRefs, activeSceneChatId, dispatch, chatsLoading]);

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------

	const onNewChat = async () => {
		if (!activeScene || isCreatingChat) return;
		if (!canEdit(activeScene)) {
			displayViewOnlyModal();
			return;
		}

		const chatId = uuid();
		const rightmostFrameX = getRightmostFrameX();
		setIsCreatingChat(true);

		try {
			await forge.createSceneChat({
				id: chatId,
				sceneId: activeScene.id,
				title: NEW_CHAT_TITLE,
				x: rightmostFrameX !== null ? rightmostFrameX + CHAT_COLUMN_GAP : 0,
				y: 0,
				metadata: { status: ChatStatus.PROCESSING, statusMessage: 'Initializing chat...' },
			});

			dispatch(setActiveSceneChatId(chatId));

			// we intentionally do NOT update the pending chat input here.  the ChatInput component
			// owns it and manages that process - ss
		} finally {
			setIsCreatingChat(false);
		}
	};

	return (
		<Base.Base ref={ref}>
			<Header
				title={activeChatTitle}
				onNewChat={onNewChat}
				chats={sortedChatRefs}
				activeChatId={activeSceneChatId}
				onSelectChat={(id: string) => dispatch(setActiveSceneChatId(id))}
				moreMenuItems={moreMenuItems}
				isLoading={isCreatingChat}
			/>
			{activeSceneChatId && (
				<Base.Conversation>
					{chatsLoading || isCreatingChat ? (
						<Base.LoadingContainer>
							<Loader size={32} />
						</Base.LoadingContainer>
					) : (
						<SceneConversation />
					)}
				</Base.Conversation>
			)}
		</Base.Base>
	);
};

export default SceneChatPanel;
